import React, { useState, useEffect } from 'react';
import {
    Card, CardContent, Typography, Grid, Box, Button, IconButton, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Alert, CircularProgress, Switch, FormControlLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CropSquare as YellowCardIcon,
    Stop as RedCardIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import { useNotify } from 'react-admin';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

const TipoCartellinoManager = ({ legaId, sportId }) => {
    const notify = useNotify();
    const [tipiCartellino, setTipiCartellino] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTipo, setEditingTipo] = useState(null);
    const [formData, setFormData] = useState({
        nome: '',
        note: '',
        flVisibile: true,
        flAttivo: true,
        ordinamento: 0
    });

    // Carica i tipi di cartellino per lo sport della lega
    const loadTipiCartellino = async () => {
        if (!sportId) return;

        try {
            setLoading(true);
            const jwt = getJwt();

            const response = await axios.get(`${host}/tbl-tipocartellino`, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    sportId: sportId,
                    $sort: { ordinamento: 1, nome: 1 }
                }
            });

            setTipiCartellino(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento tipi cartellino:", error);
            notify("Errore nel caricamento dei tipi cartellino", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTipiCartellino();
    }, [sportId]);

    // Apri dialog per nuovo tipo cartellino
    const handleAdd = () => {
        setEditingTipo(null);
        setFormData({
            nome: '',
            note: '',
            flVisibile: true,
            flAttivo: true,
            ordinamento: tipiCartellino.length + 1
        });
        setDialogOpen(true);
    };

    // Apri dialog per modifica tipo cartellino
    const handleEdit = (tipo) => {
        setEditingTipo(tipo);
        setFormData({
            nome: tipo.nome || '',
            note: tipo.note || '',
            flVisibile: Boolean(tipo.flVisibile),
            flAttivo: Boolean(tipo.flAttivo),
            ordinamento: tipo.ordinamento || 0
        });
        setDialogOpen(true);
    };

    // Salva tipo cartellino (nuovo o modifica)
    const handleSave = async () => {
        try {
            const jwt = getJwt();
            
            if (editingTipo) {
                // Modifica esistente - includi tutti i campi
                const dataToSend = {
                    ...formData,
                    sportId: sportId,
                    flVisibile: formData.flVisibile ? 1 : 0,
                    flAttivo: formData.flAttivo ? 1 : 0,
                    flAnnullato: 0
                };
                
                await axios.patch(`${host}/tbl-tipocartellino/${editingTipo.id}`, dataToSend, {
                    headers: {
                        'Authorization': `Bearer ${jwt}`,
                        'Content-Type': 'application/json'
                    }
                });
                notify("Tipo cartellino aggiornato con successo", { type: 'success' });
            } else {
                // Nuovo tipo cartellino - costruisci payload senza id
                const dataToSend = {
                    nome: formData.nome,
                    note: formData.note,
                    ordinamento: formData.ordinamento,
                    sportId: sportId,
                    flVisibile: formData.flVisibile ? 1 : 0,
                    flAttivo: formData.flAttivo ? 1 : 0,
                    flAnnullato: 0
                };
                
                await axios.post(`${host}/tbl-tipocartellino`, dataToSend, {
                    headers: {
                        'Authorization': `Bearer ${jwt}`,
                        'Content-Type': 'application/json'
                    }
                });
                notify("Tipo cartellino creato con successo", { type: 'success' });
            }

            setDialogOpen(false);
            loadTipiCartellino();
        } catch (error) {
            console.error("Errore nel salvataggio:", error);
            notify("Errore nel salvataggio del tipo cartellino", { type: 'error' });
        }
    };

    // Toggle visibilità
    const handleToggleVisibility = async (tipo) => {
        try {
            const jwt = getJwt();
            await axios.patch(`${host}/tbl-tipocartellino/${tipo.id}`, {
                flVisibile: tipo.flVisibile ? 0 : 1
            }, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                }
            });

            notify(`Tipo cartellino ${tipo.flVisibile ? 'nascosto' : 'mostrato'}`, { type: 'success' });
            loadTipiCartellino();
        } catch (error) {
            console.error("Errore nell'aggiornamento:", error);
            notify("Errore nell'aggiornamento", { type: 'error' });
        }
    };

    // Elimina tipo cartellino (soft delete)
    const handleDelete = async (tipo) => {
        if (!window.confirm(`Sei sicuro di voler eliminare il tipo cartellino "${tipo.nome}"?`)) {
            return;
        }

        try {
            const jwt = getJwt();
            await axios.patch(`${host}/tbl-tipocartellino/${tipo.id}`, {
                flAnnullato: 1
            }, {
                headers: {
                    'Authorization': `Bearer ${jwt}`,
                    'Content-Type': 'application/json'
                }
            });

            notify("Tipo cartellino eliminato", { type: 'success' });
            loadTipiCartellino();
        } catch (error) {
            console.error("Errore nell'eliminazione:", error);
            notify("Errore nell'eliminazione", { type: 'error' });
        }
    };

    // Icona per tipo cartellino
    const getCartellinoIcon = (nome) => {
        const nomeUpper = nome?.toUpperCase();
        if (nomeUpper?.includes('GIALLO')) {
            return <YellowCardIcon sx={{ color: '#ffc107' }} />;
        } else if (nomeUpper?.includes('ROSSO')) {
            return <RedCardIcon sx={{ color: '#f44336' }} />;
        }
        return <CropSquareIcon sx={{ color: '#9e9e9e' }} />;
    };

    if (!sportId) {
        return (
            <Alert severity="info">
                Seleziona prima uno sport per gestire i tipi di cartellino
            </Alert>
        );
    }

    return (
        <Card sx={{ mt: 3 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CropSquareIcon color="primary" />
                        Tipi di Cartellino
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        size="small"
                    >
                        Aggiungi Tipo
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : tipiCartellino.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Note</TableCell>
                                    <TableCell align="center">Ordine</TableCell>
                                    <TableCell align="center">Stato</TableCell>
                                    <TableCell align="center">Azioni</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tipiCartellino.map((tipo) => (
                                    <TableRow key={tipo.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getCartellinoIcon(tipo.nome)}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {tipo.nome}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {tipo.note || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={tipo.ordinamento || 0}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                {tipo.flAttivo ? (
                                                    <Chip label="Attivo" size="small" color="success" />
                                                ) : (
                                                    <Chip label="Inattivo" size="small" color="default" />
                                                )}
                                                {!tipo.flVisibile && (
                                                    <Chip label="Nascosto" size="small" color="warning" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEdit(tipo)}
                                                    title="Modifica"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleVisibility(tipo)}
                                                    title={tipo.flVisibile ? "Nascondi" : "Mostra"}
                                                >
                                                    {tipo.flVisibile ?
                                                        <VisibilityOffIcon fontSize="small" /> :
                                                        <VisibilityIcon fontSize="small" />
                                                    }
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(tipo)}
                                                    title="Elimina"
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Alert severity="info">
                        Nessun tipo di cartellino definito per questo sport. Clicca "Aggiungi Tipo" per iniziare.
                    </Alert>
                )}

                {/* Dialog per aggiungere/modificare tipo cartellino */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {editingTipo ? 'Modifica Tipo Cartellino' : 'Nuovo Tipo Cartellino'}
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome Tipo Cartellino"
                                    value={formData.nome}
                                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                    placeholder="es. GIALLO, ROSSO, VERDE..."
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Note"
                                    value={formData.note}
                                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                    multiline
                                    rows={3}
                                    placeholder="Descrizione o note aggiuntive..."
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Ordinamento"
                                    type="number"
                                    value={formData.ordinamento}
                                    onChange={(e) => setFormData({ ...formData, ordinamento: parseInt(e.target.value) || 0 })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.flVisibile}
                                            onChange={(e) => setFormData({ ...formData, flVisibile: e.target.checked })}
                                        />
                                    }
                                    label="Visibile"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.flAttivo}
                                            onChange={(e) => setFormData({ ...formData, flAttivo: e.target.checked })}
                                        />
                                    }
                                    label="Attivo"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>
                            Annulla
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!formData.nome.trim()}
                        >
                            {editingTipo ? 'Aggiorna' : 'Crea'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};

export default TipoCartellinoManager;
